import { useEffect, useState } from "react";
import { useReveal } from "../lib/useReveal";

const API = process.env.REACT_APP_BACKEND_URL;

const FALLBACK_GROUPS = [
  {
    title: "Cattle Family",
    key: "cattle",
    items: [
      { label: "Cows", count: 538 },
      { label: "Bulls", count: 6 },
      { label: "Oxen (castrated)", count: 217 },
      { label: "Male calves", count: 205 },
      { label: "Female calves", count: 215 },
      { label: "Sick / weak calves", count: 4, soft: true },
    ],
  },
  {
    title: "Buffalo Family",
    key: "buffalo",
    items: [
      { label: "Buffaloes", count: 13 },
      { label: "Male buffalo calves", count: 60 },
      { label: "Female buffalo calves", count: 10 },
    ],
  },
  {
    title: "Other Animals & Birds",
    key: "others",
    items: [
      { label: "Dogs", count: 19 },
      { label: "Horses", count: 6 },
      { label: "Camels", count: 3 },
      { label: "Rabbits", count: 17 },
      { label: "Goats", count: 1 },
      { label: "Pigeons", count: 2 },
      { label: "Peacocks", count: 1 },
    ],
  },
];

function Column({ title, items, subtotal, testid }) {
  return (
    <div data-testid={testid} className="bg-[#FDFBF7] border border-[#E3DECE] rounded-2xl p-7 md:p-8">
      <div className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37] font-semibold">
        {title}
      </div>
      <ul className="mt-6 space-y-3">
        {items.map((it) => (
          <li
            key={it.label}
            className="flex items-baseline justify-between gap-4 border-b border-dashed border-[#E3DECE] pb-3 last:border-0 last:pb-0"
          >
            <span className={`text-[15px] ${it.soft ? "text-[#7A8C81] italic" : "text-[#1A2E24]"}`}>
              {it.label}
            </span>
            <span className="font-display text-[#1B4D3E] text-2xl tabular-nums">
              {Number(it.count).toLocaleString()}
            </span>
          </li>
        ))}
      </ul>
      <div className="mt-6 pt-4 border-t border-[#1B4D3E]/20 flex items-baseline justify-between">
        <span className="text-[11px] tracking-[0.22em] uppercase text-[#4A5D53]">Subtotal</span>
        <span className="font-display italic text-[#D4AF37] text-2xl tabular-nums">
          {subtotal.toLocaleString()}
        </span>
      </div>
    </div>
  );
}

function findGroup(groups, key) {
  return groups.find((g) => g.key === key) || { title: "", items: [] };
}

export default function Census() {
  const ref = useReveal();
  const [groups, setGroups] = useState(FALLBACK_GROUPS);
  const [updatedAt, setUpdatedAt] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/api/census`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!cancelled && data && Array.isArray(data.groups) && data.groups.length) {
          setGroups(data.groups);
          setUpdatedAt(data.updated_at || null);
        }
      })
      .catch(() => {
        /* fall back silently */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const cattle = findGroup(groups, "cattle");
  const buffalo = findGroup(groups, "buffalo");
  const others = findGroup(groups, "others");

  const subtotal = (g) => g.items.reduce((a, b) => a + (Number(b.count) || 0), 0);
  const cattleTotal = subtotal(cattle);
  const buffaloTotal = subtotal(buffalo);
  const othersTotal = subtotal(others);
  const grand = cattleTotal + buffaloTotal + othersTotal;

  const lastUpdatedLabel = updatedAt
    ? new Date(updatedAt).toLocaleDateString("en-IN", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "April 2026";

  return (
    <section
      id="census"
      data-testid="census-section"
      className="py-24 md:py-32 bg-[#F4F1EA]"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        <div ref={ref} className="reveal grid lg:grid-cols-12 gap-10 lg:gap-16 items-end mb-14">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-[1px] w-10 bg-[#D4AF37]" />
              <span className="text-xs tracking-[0.3em] uppercase text-[#4A5D53]">
                Our living family today
              </span>
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#1A2E24] leading-[1.05]">
              Every life <span className="italic text-[#1B4D3E]">counted</span>.
              <br />Every life cared for.
            </h2>
            <p className="mt-6 text-[#4A5D53] leading-relaxed max-w-xl">
              A real, current census of every animal who calls Hastkesar home —
              from a single peacock to over five hundred cows. Updated by our
              caretakers and verified each month.
            </p>
          </div>

          <div className="lg:col-span-5">
            <div className="bg-[#1B4D3E] text-[#FDFBF7] rounded-2xl p-8 md:p-10 relative overflow-hidden">
              <div className="absolute -top-20 -right-20 w-60 h-60 rounded-full border border-[#D4AF37]/30 pointer-events-none" />
              <div className="absolute -bottom-20 -left-20 w-60 h-60 rounded-full border border-[#D4AF37]/20 pointer-events-none" />
              <div className="relative">
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37] font-semibold">
                  Total residents
                </div>
                <div className="mt-3 flex items-baseline gap-3">
                  <div
                    data-testid="census-total"
                    className="font-display text-[#FDFBF7] text-7xl md:text-8xl leading-none tabular-nums"
                  >
                    {grand.toLocaleString()}
                  </div>
                  <div className="font-display italic text-[#D4AF37] text-3xl">
                    lives
                  </div>
                </div>
                <div className="mt-4 text-[#FDFBF7]/75 text-sm">
                  cared for under one roof, today.
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-5">
          <Column title={cattle.title || "Cattle Family"} items={cattle.items} subtotal={cattleTotal} testid="census-cattle" />
          <Column title={buffalo.title || "Buffalo Family"} items={buffalo.items} subtotal={buffaloTotal} testid="census-buffalo" />
          <Column title={others.title || "Other Animals & Birds"} items={others.items} subtotal={othersTotal} testid="census-others" />
        </div>

        <p className="mt-10 text-center text-xs text-[#7A8C81] tracking-wider uppercase">
          Census updated {lastUpdatedLabel} · Verified by Hastkesar caretakers
        </p>
      </div>
    </section>
  );
}
